// InstallBanner — invita a instalar la app como PWA (crítico en iOS Safari
// para que localStorage y sesiones persistan entre aperturas).
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Share, Plus, Smartphone } from "lucide-react";

const DISMISS_KEY = "install-banner-dismissed-v1";

function isStandalone() {
  // iOS Safari uses navigator.standalone; modern PWA uses display-mode
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isiOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

export default function InstallBanner({ variant = "compact" }) {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (localStorage.getItem(DISMISS_KEY)) {
      setDismissed(true);
    }
  }, []);

  if (isStandalone() || dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-full mt-4 mb-2 p-3 rounded-2xl border border-amber-300 bg-amber-50 text-left flex items-start gap-3 active:scale-[0.99] transition-transform"
        data-testid="install-banner-cta"
      >
        <Smartphone size={18} className="text-amber-700 shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-amber-900 leading-tight">
            📱 Instala la app en tu iPhone
          </p>
          <p className="text-xs text-amber-800 mt-1 leading-snug">
            Evita tener que volver a entrar con Google cada vez. Toca aquí para ver cómo.
          </p>
        </div>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[1200] bg-black/60 flex items-end justify-center"
            onClick={() => setOpen(false)}
            data-testid="install-modal-backdrop"
          >
            <motion.div
              initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-[hsl(var(--bg))] rounded-t-3xl shadow-2xl"
              style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)" }}
              data-testid="install-modal"
            >
              <div className="px-5 py-4 border-b border-[hsl(var(--line))] flex items-center justify-between">
                <h2 className="text-base font-bold">📱 Instalar en iPhone</h2>
                <button onClick={() => setOpen(false)} className="text-[hsl(var(--muted))]">
                  <X size={20} />
                </button>
              </div>

              <div className="px-5 py-5">
                {!isiOS() && (
                  <p className="text-sm text-[hsl(var(--ink-soft))] leading-relaxed mb-4">
                    Estas instrucciones son para iPhone (Safari). En Android: abre el menú ⋮ del navegador y elige "Instalar app" o "Añadir a pantalla principal".
                  </p>
                )}

                <p className="text-sm text-[hsl(var(--ink-soft))] leading-relaxed mb-5">
                  Solo una vez. Te tomará 20 segundos y resolverá el problema del login.
                </p>

                <ol className="space-y-4">
                  <li className="flex gap-3">
                    <span className="w-7 h-7 shrink-0 rounded-full bg-[hsl(var(--accent))] text-white text-sm font-bold flex items-center justify-center">1</span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold leading-snug">
                        Abre esta página en <strong>Safari</strong>
                      </p>
                      <p className="text-xs text-[hsl(var(--muted))] mt-0.5 leading-relaxed">
                        Si estás abriéndola desde Notas, mantén presionado el link → "Abrir en Safari".
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-7 h-7 shrink-0 rounded-full bg-[hsl(var(--accent))] text-white text-sm font-bold flex items-center justify-center">2</span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold leading-snug">
                        Toca el botón <Share size={14} className="inline mb-0.5" /> <strong>Compartir</strong>
                      </p>
                      <p className="text-xs text-[hsl(var(--muted))] mt-0.5 leading-relaxed">
                        Está abajo en el centro de la barra de Safari.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-7 h-7 shrink-0 rounded-full bg-[hsl(var(--accent))] text-white text-sm font-bold flex items-center justify-center">3</span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold leading-snug">
                        Elige <Plus size={14} className="inline mb-0.5" /> <strong>"Agregar a inicio"</strong>
                      </p>
                      <p className="text-xs text-[hsl(var(--muted))] mt-0.5 leading-relaxed">
                        En inglés: "Add to Home Screen". Desplázate un poco si no lo ves.
                      </p>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-7 h-7 shrink-0 rounded-full bg-[hsl(var(--accent))] text-white text-sm font-bold flex items-center justify-center">4</span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold leading-snug">
                        Toca <strong>"Agregar"</strong> arriba a la derecha
                      </p>
                      <p className="text-xs text-[hsl(var(--muted))] mt-0.5 leading-relaxed">
                        Aparecerá un ícono en tu pantalla de inicio. ✓
                      </p>
                    </div>
                  </li>
                </ol>

                <div className="mt-6 rounded-xl bg-[hsl(140_40%_96%)] border border-[hsl(140_40%_85%)] p-3.5">
                  <p className="text-xs text-[hsl(140_40%_25%)] leading-relaxed">
                    <strong>Después:</strong> abre la app desde el ícono nuevo (no más desde Notas). El PIN va a funcionar sin pasarte a Google.
                  </p>
                </div>

                <button
                  onClick={handleDismiss}
                  className="block mx-auto mt-5 text-xs text-[hsl(var(--muted))] underline"
                  data-testid="install-dismiss"
                >
                  Ya lo instalé / No mostrar más
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
